<template>
  <div class="">
    <position-select-modal
      v-if="selectForLineIndex !== null"
      :date="date"
      @close="selectForLineIndex = null"
      @selected="selected($event)"
    />
    <div class="border-b p-4" v-for="(line, index) in lines" :key="line.id">
      <div
        class="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-2"
      >
        <sqr-input-select
          class="md:w-20"
          :label="$t('invoice_line_tarif')"
          :value="line.tarif"
          :options="tarifs"
          @change="change(index, line.id, 'tarif', $event)"
          :v="v && v.$each[index].tarif"
        />
        <div
          v-if="line.tarif === '999'"
          class="flex-1 md:grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-y-3 md:gap-y-0 md:gap-x-2"
        >
          <sqr-input-text
            :label="$t('invoice_line_label')"
            :value="line.label"
            @change="change(index, line.id, 'label', $event)"
            :v="v && v.$each[index].label"
          />
          <sqr-input-text
            :label="$t('invoice_line_description')"
            :value="line.description"
            @change="change(index, line.id, 'description', $event)"
            :v="v && v.$each[index].description"
          />
        </div>
        <sqr-input-display
          class="flex-1"
          v-else
          :label="$t('invoice_line_code')"
          :value="lineLabel(line)"
          @click="selectForLineIndex = index"
          :v="v && v.$each[index].code"
        />
        <sqr-input-number
          v-if="useMinutes && line.tarif !== '999'"
          type="integer"
          class="md:w-20"
          :label="$t('invoice_line_minutes')"
          :value="line.minutes"
          @change="change(index, line.id, 'minutes', $event)"
          :v="v && v.$each[index].quantity"
          step="5"
        />
        <sqr-input-number
          v-if="useQuantity || line.tarif === '999'"
          type="int"
          class="md:w-20"
          :label="$t('invoice_quantity')"
          :value="line.quantity"
          @change="change(index, line.id, 'quantity', $event)"
          :v="v && v.$each[index].quantity"
          step="1"
        />
        <sqr-input-amount
          v-if="useMinutes && line.tarif !== '999'"
          class="md:w-32"
          :label="$t('invoice_line_hourly')"
          :value="line.hourly"
          @change="change(index, line.id, 'hourly', $event)"
          :v="v && v.$each[index].unitPrice"
        />
        <sqr-input-amount
          v-if="useQuantity || line.tarif === '999'"
          class="md:w-32"
          :label="$t('invoice_unit_price')"
          :value="line.unitPrice"
          @change="change(index, line.id, 'unitPrice', $event)"
          :v="v && v.$each[index].unitPrice"
          :default="{ amount: 0, precision: 4, currency: 'CHF' }"
          :precision="4"
        />
        <sqr-input-percent
          v-if="!noTaxes"
          class="md:w-24"
          :label="$t('invoice_vat_rate')"
          :value="line.vatRate"
          @change="change(index, line.id, 'vatRate', $event)"
          :v="v && v.$each[index].vatRate"
        />
        <sqr-input-amount
          v-if="!noAmounts"
          class="hidden md:block md:w-32"
          :label="$t('invoice_line_amount')"
          :value="line.amount"
          readonly
        />
        <div class="flex flex-col justify-start md:justify-end items-start">
          <sqr-button
            class="hidden md:block"
            color="transparent"
            second="red"
            :tooltip="$t('buttons_remove')"
            @click="$emit('remove', { index, id: line.id })"
          >
            <template slot="icon">
              <XIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>
          <sqr-button
            class="md:hidden"
            color="red"
            light
            :label="$t('invoice_line_remove')"
            @click="$emit('remove', { index, id: line.id })"
          >
            <template slot="icon">
              <XIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>
        </div>
      </div>
    </div>
    <div class="p-4 flex flex-wrap md:flex-row md:space-x-2 md:items-center">
      <sqr-button
        :label="$t('invoice_line_add')"
        @click="$emit('add', lineDefault)"
        color="green"
        second="yellow"
        light
      >
        <template slot="icon">
          <PlusIcon class="inline w-4 h-4 stroke-current" />
        </template>
      </sqr-button>
      <sqr-button
        v-for="model in models"
        :key="model.id"
        color="transparent"
        second="green"
        :label="model.name"
        @click="modelAdd(model)"
      >
        <template slot="icon">
          <PlusIcon class="inline w-4 h-4 stroke-current" />
        </template>
      </sqr-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import SqrInputSelect from '@/components/SqrInputSelect';
import SqrInputDisplay from '@/components/SqrInputDisplay';
import SqrInputText from '@/components/SqrInputText.vue';
import SqrInputNumber from '@/components/SqrInputNumber.vue';
import SqrInputAmount from '@/components/SqrInputAmount.vue';
import SqrInputPercent from '@/components/SqrInputPercent.vue';
import SqrButton from '@/components/SqrButton.vue';

import catalog from '@/data/catalog';
import PositionSelectModal from '@/components/PositionSelectModal';

import amount from '@/filters/amount';
import { PlusIcon, XIcon } from '@vue-hero-icons/outline';

export default {
  name: 'InvoiceLinesForm',
  components: {
    PositionSelectModal,
    SqrInputSelect,
    SqrInputDisplay,
    SqrInputText,
    SqrInputNumber,
    SqrInputAmount,
    SqrInputPercent,
    SqrButton,
    XIcon,
    PlusIcon
  },
  filters: {
    amount
  },
  props: {
    date: String,
    lines: { type: Array, default: () => [] },
    v: Object,
    noAmounts: Boolean,
    noModels: Boolean,
    noTaxes: Boolean
  },
  computed: {
    ...mapGetters('account', ['account']),
    useQuantity() {
      return this.account && this.account.useQuantity;
    },
    useMinutes() {
      return !this.useQuantity;
    },
    lineDefault() {
      if (this.account) {
        return {
          vatRate: this.account.vatRate || 0.0
        };
      } else {
        return {};
      }
    },
    models() {
      return this.noModels ? [] : this.account?.models;
    },
    tarifs() {
      return catalog.tarifs.map(t => ({
        id: t.id,
        label: t.id
      }));
    },
    positions() {
      return catalog.positions.map(p => ({
        id: p.id,
        tarif: p.tarif,
        label: [p.id, p.label.fr].join(' '),
        label18: p.label
      }));
    },
    lang() {
      return this.$i18n.locale;
    }
  },
  data() {
    return {
      selectForLineIndex: null
    };
  },
  methods: {
    invoiceChange(field, value) {
      this.$emit('invoice-change', { field, value });
    },
    change(index, id, field, value) {
      this.$emit('change', { index, id, field, value });
    },
    modelAdd(model) {
      for (const line of model.lines) {
        this.$emit('add', { ...line });
      }
    },
    selected(position) {
      if (position) {
        this.$emit('change', {
          index: this.selectForLineIndex,
          data: {
            tarif: position.tarif,
            code: position.id,
            label18: position.label18
          }
        });
      }
      this.selectForLineIndex = null;
    },
    lineLabel(line) {
      if (!line) return '';
      const code = line.code;
      const label = (line.label18 && line.label18[this.lang]) || line.label;
      return [code, label].filter(v => Boolean(v)).join(' ');
    }
  }
};
</script>
